import * as Sentry from '@sentry/nextjs'
import { isTransientInfrastructureError } from '@/lib/errors'

type NormalizedSentryValue =
  | string
  | number
  | boolean
  | null
  | NormalizedSentryValue[]
  | { [key: string]: NormalizedSentryValue }

function normalizeSentryValue(
  value: unknown,
  seen: WeakSet<object>,
  depth = 0
): NormalizedSentryValue {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value
  }
  if (typeof value === 'bigint' || typeof value === 'symbol') {
    return String(value)
  }
  if (typeof value === 'function') {
    return `[function ${value.name || 'anonymous'}]`
  }
  if (value instanceof Error) {
    return {
      message: value.message,
      name: value.name,
      stack: value.stack ?? null,
    }
  }
  if (depth >= 4) {
    return '[max-depth]'
  }
  if (Array.isArray(value)) {
    return value.slice(0, 20).map((entry) => normalizeSentryValue(entry, seen, depth + 1))
  }
  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[circular]'
    }

    seen.add(value)

    const entries = Object.entries(value).slice(0, 20)
    return Object.fromEntries(
      entries.map(([key, entry]) => [key, normalizeSentryValue(entry, seen, depth + 1)])
    )
  }

  return String(value)
}

function buildMessage(args: unknown[]): string {
  const message = args.filter((arg): arg is string => typeof arg === 'string').join(' ').trim()
  if (message) return message

  const firstError = args.find((arg): arg is Error => arg instanceof Error)
  if (firstError) return firstError.message

  return 'logger.error called'
}

function getThrowable(args: unknown[]): Error | undefined {
  return args.find((arg): arg is Error => arg instanceof Error)
}

function shouldSkipLoggerCapture(args: unknown[]): boolean {
  return args.some((arg) => isTransientInfrastructureError(arg))
}

export function captureLoggerError(args: unknown[]): void {
  if (args.length === 0) return
  if (shouldSkipLoggerCapture(args)) return

  const normalizedArgs = args.map((arg) => normalizeSentryValue(arg, new WeakSet<object>()))
  const captureContext = {
    extra: {
      loggerArgs: normalizedArgs,
    },
  }

  const throwable = getThrowable(args)
  if (throwable) {
    Sentry.captureException(throwable, captureContext)
    return
  }

  const contextObject = args.find(
    (arg): arg is Record<string, unknown> =>
      typeof arg === 'object' && arg !== null && !Array.isArray(arg)
  )
  if (contextObject && isTransientInfrastructureError(contextObject)) {
    return
  }

  Sentry.captureMessage(buildMessage(args), {
    ...captureContext,
    level: 'error',
  })
}
